interface TripRequest {
  startDate: string
  endDate: string
  travelers: number
  locations: string[]
}

export async function generateTripSuggestions(tripData: TripRequest) {
  try {
    const startDate = new Date(tripData.startDate)
    const endDate = new Date(tripData.endDate)
    const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

    // Validate input data
    if (duration <= 0) {
      throw new Error("End date must be after start date")
    }

    if (tripData.travelers <= 0) {
      throw new Error("Number of travelers must be greater than 0")
    }

    if (tripData.locations.length === 0) {
      throw new Error("At least one location must be selected")
    }

    // Format dates for the AI prompt
    const formatDate = (date: Date) => {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
    }

    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer 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`,
        // Authorization: `Bearer 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`,
      },
      body: JSON.stringify({
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content: `You are an expert Telangana tourism guide. Create detailed day-by-day itineraries in a clean JSON format without any markdown formatting.

CRITICAL: Return ONLY a valid JSON object with NO markdown, NO asterisks, NO special characters. Use plain text only.

{
  "itinerary": {
    "title": "Telangana Travel Itinerary",
    "duration": 2,
    "travelers": 4,
    "locations": ["Hyderabad", "Warangal"],
    "days": [
      {
        "dayNumber": 1,
        "date": "Jul 27, 2023",
        "title": "Arrival and Exploration",
        "city": "Hyderabad",
        "schedule": [
          {
            "timeSlot": "9:00 AM - 1:00 PM",
            "period": "Morning and Midday",
            "activities": [
              {
                "time": "9:00 AM",
                "activity": "Arrival at Rajiv Gandhi International Airport",
                "location": "Hyderabad Airport",
                "type": "Transport",
                "cost": "500",
                "duration": "30 mins",
                "tips": "Pre-book taxi for convenience"
              },
              {
                "time": "10:00 AM",
                "activity": "Check-in at Hotel Taj Krishna",
                "location": "Banjara Hills",
                "type": "Accommodation",
                "cost": "8000",
                "duration": "30 mins",
                "tips": "Early check-in available on request"
              }
            ]
          }
        ],
        "meals": [
          {
            "type": "Breakfast",
            "restaurant": "Paradise Restaurant",
            "location": "Secunderabad",
            "cuisine": "Hyderabadi",
            "cost": "300",
            "speciality": "Haleem, Biryani",
            "timing": "8:00 AM - 11:00 AM"
          }
        ],
        "accommodation": {
          "name": "Hotel Taj Krishna",
          "location": "Banjara Hills",
          "type": "Luxury",
          "cost": "8000",
          "rating": "5 stars",
          "amenities": ["Pool", "Spa", "Gym", "WiFi"]
        }
      }
    ],
    "budget": {
      "accommodation": "16000",
      "food": "2400",
      "transport": "1500",
      "attractions": "800",
      "total": "20700"
    },
    "essentials": {
      "bestTime": "October to March",
      "weather": "Pleasant, 20-30 degrees Celsius",
      "clothing": "Light cotton clothes",
      "language": "Telugu, Hindi, English"
    }
  }
}

FORMATTING RULES:
- NO asterisks, NO markdown, NO special characters
- Use plain text only
- All costs should be numbers without rupee symbol
- Return ONLY valid JSON
- Include specific restaurant names, hotels, and attractions
- Add realistic costs in numbers only
- Include practical tips for each activity
- Specify activity types: Transport, Sightseeing, Food, Shopping, Cultural, Accommodation
- Add duration for each activity`,
          },
          {
            role: "user",
            content: `Create a comprehensive ${duration}-day travel itinerary for ${tripData.travelers} ${tripData.travelers === 1 ? "traveler" : "travelers"} visiting Telangana from ${formatDate(startDate)} to ${formatDate(endDate)}.

Selected destinations: ${tripData.locations.join(", ")}

Return clean JSON with:
- Daily schedules with time slots and activities
- Restaurant recommendations with costs and specialties
- Accommodation suggestions with amenities
- Budget breakdown by category
- Essential travel information
- NO markdown formatting, NO asterisks, NO special characters
- Plain text only in all fields`,
          },
        ],
        max_tokens: 4000,
        temperature: 0.7,
      }),
    })

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`)
    }

    const data = await response.json()
    let suggestions = data.choices[0]?.message?.content || "No suggestions generated"

    // Clean up the response to ensure it's valid JSON and remove markdown
    suggestions = suggestions
      .replace(/```json\n?/g, "")
      .replace(/```\n?/g, "")
      .replace(/\*\*/g, "") // Remove bold markdown
      .replace(/\*/g, "") // Remove italic markdown
      .replace(/#{1,6}\s/g, "") // Remove heading markdown
      .replace(/\[([^\]]+)\]$$[^)]+$$/g, "$1") // Convert links to plain text
      .trim()

    return {
      success: true,
      suggestions,
      metadata: {
        duration,
        travelers: tripData.travelers,
        locations: tripData.locations,
        generatedAt: new Date().toISOString(),
      },
    }
  } catch (error) {
    console.error("Error generating trip suggestions:", error)

    if (error instanceof Error) {
      if (error.message.includes("API key")) {
        return {
          success: false,
          error: "OpenAI API configuration error. Please check your API key.",
          suggestions: "",
        }
      }

      if (error.message.includes("rate limit")) {
        return {
          success: false,
          error: "Too many requests. Please try again in a few minutes.",
          suggestions: "",
        }
      }

      if (error.message.includes("quota")) {
        return {
          success: false,
          error: "API quota exceeded. Please check your OpenAI billing.",
          suggestions: "",
        }
      }

      return {
        success: false,
        error: error.message,
        suggestions: "",
      }
    }

    return {
      success: false,
      error: "An unexpected error occurred while generating suggestions.",
      suggestions: "",
    }
  }
}

